import _Vue from "vue";
import localization from "./LocalizationStore/localization";

export interface LanguageItem {
    Id: number;
    Code: string;
    Name: string;
    IsHide: boolean;
}

export class CacheProvider {
    private excludedLanguages = ['lv'];
    private priorityLanguages = ['ru', 'en', 'ro'];
    private disabledLanguagesKey = 'disabled_languages';
    private customLanguagesKey = 'custom_languages';
    private removedLanguagesKey = 'removed_languages';

    private normalizeLanguages(languages: string[]): string[] {
        const result: string[] = [];
        (languages || []).forEach((langCode) => {
            const normalizedLangCode = (langCode || '').toString().trim();
            const normalizedLangKey = normalizedLangCode.toLowerCase();
            const isExcluded = this.excludedLanguages.indexOf(normalizedLangKey) !== -1;
            const alreadyAdded = result.some((item) => item.toLowerCase() === normalizedLangKey);
            if (normalizedLangCode && !isExcluded && !alreadyAdded) {
                result.push(normalizedLangCode);
            }
        });
        const priorityResult: string[] = [];
        this.priorityLanguages.forEach((priorityLangCode) => {
            const language = result.find(
                (item) => item.toLowerCase() === priorityLangCode
            );
            if (language) {
                priorityResult.push(language);
            }
        });
        return priorityResult.concat(
            result.filter(
                (item) =>
                    this.priorityLanguages.indexOf(item.toLowerCase()) === -1
            )
        );
    }
    private getFullLanguages(languages: string[]): string[] {
        const removedLanguages = this.getRemovedLanguages();
        return this.normalizeLanguages([
            ...localization.getLangCodes(),
            ...this.getCustomLanguages(),
            ...(languages || [])
        ]).filter((langCode) => removedLanguages.indexOf(langCode.toLowerCase()) === -1);
    }
    private getCustomLanguages(): string[] {
        try {
            const value = JSON.parse(localStorage.getItem(this.customLanguagesKey) || '[]');
            return Array.isArray(value)
                ? this.normalizeLanguages(value.map((item) => (item || '').toString()))
                : [];
        } catch {
            return [];
        }
    }
    isCustomLanguage(langCode: string): boolean {
        const normalizedLangCode = (langCode || '').toString().trim().toLowerCase();
        return this.getCustomLanguages().some(
            (item) => item.toLowerCase() === normalizedLangCode
        );
    }
    private getRemovedLanguages(): string[] {
        try {
            const value = JSON.parse(localStorage.getItem(this.removedLanguagesKey) || '[]');
            return Array.isArray(value)
                ? value.map((item) => (item || '').toString().trim().toLowerCase()).filter(Boolean)
                : [];
        } catch {
            return [];
        }
    }
    addLanguage(langCode: string): boolean {
        const normalizedLangCode = (langCode || '').toString().trim().toLowerCase();
        if (!/^[a-z]{2,3}(?:-[a-z0-9]{2,8})?$/.test(normalizedLangCode)) {
            return false;
        }
        const removedLanguages = this.getRemovedLanguages();
        if (removedLanguages.indexOf(normalizedLangCode) !== -1) {
            localStorage.setItem(
                this.removedLanguagesKey,
                JSON.stringify(removedLanguages.filter((item) => item !== normalizedLangCode))
            );
            this.setLanguageEnabled(normalizedLangCode, true);
            return true;
        }
        const allLanguages = this.getFullLanguages([]);
        if (allLanguages.some((item) => item.toLowerCase() === normalizedLangCode)) {
            return false;
        }
        const customLanguages = this.getCustomLanguages();
        customLanguages.push(normalizedLangCode);
        localStorage.setItem(this.customLanguagesKey, JSON.stringify(customLanguages));
        this.setLanguageEnabled(normalizedLangCode, true);
        return true;
    }
    removeLanguage(langCode: string): boolean {
        const normalizedLangCode = (langCode || '').toString().trim().toLowerCase();
        if (!normalizedLangCode) {
            return false;
        }
        const removedLanguages = this.getRemovedLanguages().filter(
            (item) => item !== normalizedLangCode
        );
        removedLanguages.push(normalizedLangCode);
        localStorage.setItem(this.removedLanguagesKey, JSON.stringify(removedLanguages));
        this.setLanguageEnabled(normalizedLangCode, true);
        return true;
    }
    private getDisabledLanguages(): string[] {
        try {
            const value = JSON.parse(localStorage.getItem(this.disabledLanguagesKey) || '[]');
            return Array.isArray(value)
                ? value.map((item) => (item || '').toString().trim().toLowerCase()).filter(Boolean)
                : [];
        } catch {
            return [];
        }
    }
    isLanguageEnabled(langCode: string): boolean {
        const normalizedLangCode = (langCode || '').toString().trim().toLowerCase();
        return this.getDisabledLanguages().indexOf(normalizedLangCode) === -1;
    }
    setLanguageEnabled(langCode: string, enabled: boolean) {
        const normalizedLangCode = (langCode || '').toString().trim().toLowerCase();
        const disabledLanguages = this.getDisabledLanguages().filter(
            (item) => item !== normalizedLangCode
        );
        if (!enabled && normalizedLangCode) {
            disabledLanguages.push(normalizedLangCode);
        }
        localStorage.setItem(this.disabledLanguagesKey, JSON.stringify(disabledLanguages));
    }
    private hasAllDefaultLanguages(languages: string[]): boolean {
        const defaultLanguages = localization.getLangCodes();
        return defaultLanguages.every((langCode) => (languages || []).indexOf(langCode) !== -1);
    }
    private normalizeLanguageItems(value: any): LanguageItem[] {
        const source = Array.isArray(value) ? value : value ? [value] : [];
        const result: LanguageItem[] = [];
        source.forEach((item) => {
            const langCode = typeof item === 'string'
                ? item
                : (item && (item.Code || item.LangCode || item.langCode || item.code)) || '';
            const code = (langCode || '').toString().trim().toLowerCase();
            if (!code) {
                return;
            }
            const normalizedCode = code.toLowerCase();
            if (this.excludedLanguages.indexOf(normalizedCode) !== -1) {
                return;
            }
            if (result.some((language) => language.Code.toLowerCase() === normalizedCode)) {
                return;
            }
            result.push({
                Id: typeof item === 'string' ? 0 : Number(item.Id || item.id || 0),
                Code: code,
                Name: typeof item === 'string'
                    ? code
                    : ((item.Name || item.name || code) || '').toString(),
                IsHide: typeof item === 'string'
                    ? this.getDisabledLanguages().indexOf(normalizedCode) !== -1
                    : Boolean(item.IsHide || item.isHide)
            });
        });
        const orderedCodes = this.normalizeLanguages(result.map((item) => item.Code));
        return orderedCodes
            .map((code) => result.find((item) => item.Code.toLowerCase() === code.toLowerCase()))
            .filter((item) => Boolean(item)) as LanguageItem[];
    }
    private buildFallbackLanguageItems(languages: string[]): LanguageItem[] {
        return this.getFullLanguages(languages).map((code) => ({
            Id: 0,
            Code: code,
            Name: code,
            IsHide: !this.isLanguageEnabled(code)
        }));
    }
    private saveLanguageItemsCash(items: LanguageItem[]) {
        this.saveCash('cached_language_items', items, 60 * 60);
        this.saveCash('cached_languages', items.map((item) => item.Code), 60 * 60);
    }
    clearLanguagesCache() {
        this.clearCash('cached_language_items');
        this.clearCash('cached_languages');
    }
    private saveCash<T>(key: string, obj: T, lifetimeSeconds: number) {
        const secondsNow = new Date().getTime();
        const data = {expired: secondsNow + lifetimeSeconds*1000, data: obj};
        localStorage.setItem(key, JSON.stringify(data));
    }
    private clearCash(key: string) {
        localStorage.removeItem(key);
    }
    private getCash<T>(key: string): T {
        const data = localStorage.getItem(key);
        if (!data) {
            return null;
        }
        const obj = JSON.parse(data) as {expired: number, data: T};
        const date = new Date();
        if (obj.expired < date.getTime()) {
            this.clearCash(key);
            return null;
        }
        return obj.data;
    }
    async getAllLanguagesAsync(): Promise<string[]> {
        const items = await this.getLanguageItemsAsync();
        return items.map((item) => item.Code);
    }
    async getLanguageItemsAsync(): Promise<LanguageItem[]> {
        const key = 'cached_language_items';
        return new Promise<LanguageItem[]>(async (resolve) => {
            const cachedItems = this.getCash<LanguageItem[]>(key);
            try {
                const res = await _Vue.$api.LanguageService.getLanguagesAsync();
                const serverLanguages = this.normalizeLanguageItems(res);
                const languages = serverLanguages.length
                    ? serverLanguages
                    : this.buildFallbackLanguageItems([]);
                this.saveLanguageItemsCash(languages);
                resolve(languages);
            } catch {
                const fallbackLanguages = this.buildFallbackLanguageItems(
                    cachedItems ? cachedItems.map((item) => item.Code) : []
                );
                this.saveLanguageItemsCash(fallbackLanguages);
                resolve(fallbackLanguages);
            }
        })
    }
    async getLanguagesAsync(): Promise<string[]> {
        const languages = await this.getLanguageItemsAsync();
        const enabledLanguages = languages
            .filter((item) => !item.IsHide)
            .map((item) => item.Code);
        return enabledLanguages.length ? enabledLanguages : languages.slice(0, 1).map((item) => item.Code);
    }
    async saveLanguageAsync(item: LanguageItem): Promise<Boolean> {
        const code = (item.Code || '').toString().trim().toLowerCase();
        if (!/^[a-z]{2,3}(?:-[a-z0-9]{2,8})?$/.test(code)) {
            return false;
        }
        const result = await _Vue.$api.LanguageService.updateLanguageAsync({
            Id: item.Id || null,
            LangCode: code,
            Name: (item.Name || code).toString().trim(),
            IsHide: Boolean(item.IsHide)
        });
        this.clearLanguagesCache();
        return result;
    }
    async deleteLanguageAsync(id: number): Promise<Boolean> {
        if (!id) {
            return false;
        }
        const result = await _Vue.$api.LanguageService.deleteLanguageAsync(id);
        this.clearLanguagesCache();
        return result;
    }
}


export default {
     install(Vue: typeof _Vue) {  
        const provider = new CacheProvider();
        Vue.prototype.$cacheProvider = provider;
        (Vue as any).$cacheProvider = provider;
    }
}
